<script setup>
import { ref, computed, onMounted, onActivated } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from './erros';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAdmin } from 'dashboard/composables/useAdmin';

import powerAgentsApi from 'dashboard/api/powerAgents';
import {
  DEFAULT_MODEL_PRIMARY,
  DEFAULT_MODEL_FALLBACK,
  TIERS,
  findModel,
} from './modelCatalog';
import Label from 'dashboard/components-next/label/Label.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import AgentSparkline from './AgentSparkline.vue';
import ChannelMarks from './ChannelMarks.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';

const { t } = useI18n();

// O card do super admin dizia o id cru do modelo em 11px. A equipe pensa em faixa e em quanto o
// agente pensa, então o chip diz "Faixa 1 · Médio"; o par completo fica no título (hover).
const nomesDaFaixa = computed(() => ({
  1: t('POWER_AGENTS.MODELS.TIER_1_SHORT'),
  2: t('POWER_AGENTS.MODELS.TIER_2_SHORT'),
}));
const nomesDoNivel = computed(() => ({
  none: t('POWER_AGENTS.REASONING.OPTIONS.NONE'),
  low: t('POWER_AGENTS.REASONING.OPTIONS.LOW'),
  medium: t('POWER_AGENTS.REASONING.OPTIONS.MEDIUM'),
  high: t('POWER_AGENTS.REASONING.OPTIONS.HIGH'),
}));
const nomeDoModelo = id => findModel(id)?.label || id;
const chipDoModelo = agent => {
  const faixa = TIERS.find(
    x =>
      x.primary === agent.model_primary && x.fallback === agent.model_fallback
  );
  return t('POWER_AGENTS.CARD.MODEL_CHIP', {
    faixa: faixa
      ? nomesDaFaixa.value[faixa.tier]
      : t('POWER_AGENTS.MODELS.CUSTOM'),
    nivel:
      nomesDoNivel.value[agent.reasoning_effort] ||
      t('POWER_AGENTS.REASONING.DEFAULT_SHORT'),
  });
};
const tituloDoModelo = agent =>
  t('POWER_AGENTS.CARD.MODEL_CHIP_TITLE', {
    principal: nomeDoModelo(agent.model_primary),
    reserva: nomeDoModelo(agent.model_fallback) || '—',
  });
const route = useRoute();
const router = useRouter();

const accountId = () => route.params.accountId;

const agents = ref([]);
const searchQuery = ref('');
const isFetching = ref(false);
const rowLoading = ref({});

const createDialogRef = ref(null);
const deleteDialogRef = ref(null);

// PA-01 (D17): criar e apagar são exclusivos do super-admin — o backend recusa com 403
// para a faixa de cliente. Esconder os botões aqui é cortesia, não segurança: quem manda
// é o guard do controller, e este componente só evita oferecer o que vai ser negado.
const { isSuperAdmin } = useAdmin();
const selectedAgent = ref(null);
const createForm = ref({ bot_name: '' });

// O slug é identidade INTERNA: o runtime chaveia revisão e webhook por ele, e o PowerData o
// guarda em power_data_connections. Quem cria um agente não deveria precisar saber disso —
// nem poder escolher um valor que quebra integração depois. Derivamos do nome, e o backend
// resolve colisão (a validação de unicidade continua lá).
const slugDoNome = nome =>
  nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `agente-${Date.now()}`;
const isCreating = ref(false);

const filteredAgents = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agents.value;
  return picoSearch(agents.value, query, ['slug', 'bot_name']);
});

// A régua da migração: quanto desta conta o OS já atende de verdade.
//
// Sai da lista que já está carregada — não custa requisição nenhuma. Existe porque esse
// número não existia em tela nenhuma: descobrir que a frota inteira estava em 2 agentes de 18
// custou seis consultas ao banco de produção, e enquanto isso a palavra "publicado" aparecia
// verde em seis agentes que o n8n atendia.
const regua = computed(() => {
  const total = agents.value.length;
  if (!total) return null;

  const quantos = valor => agents.value.filter(a => a.brain === valor).length;
  // Sem POWERAGENTS_URL o backend responde `desconhecido` para todos, e a régua cala em vez
  // de afirmar: o ambiente mal configurado é justamente onde um número errado convence.
  if (quantos('desconhecido') === total) return { indefinida: true };

  return {
    nativos: quantos('nativo'),
    externos: quantos('externo'),
    inertes: quantos('inativo'),
    total,
  };
});

// Status priority: a failed publish is the most actionable signal, then pending
// edits (live version is stale), then the lifecycle. A published-but-disabled
// agent shows Disabled — publish_status alone doesn't mean it routes traffic.
const statusKey = agent => {
  // Ação em curso primeiro: são os dois estados que mudam sozinhos em segundos.
  if (agent.publish_status === 'failed') return 'FAILED';
  if (agent.publish_status === 'provisioning') return 'PUBLISHING';
  // Depois, QUEM ATENDE — antes de todo o ciclo de vida do publish.
  //
  // Um agente atendido por fora está respondendo cliente agora, e `pending_changes` /
  // `needs_republish` falam da cópia que o runtime do OS guarda — que, nesse caso, não atende
  // ninguém. Visto na conta 2 do preview: a régua dizia "1 atendido por fora" e o card do
  // mesmo agente dizia "Não publicado". Duas frases verdadeiras, uma tela mentindo.
  if (agent.brain === 'externo') return 'PUBLISHED_LEGACY';
  if (agent.pending_changes) return 'UNPUBLISHED';
  // Para quando NADA mudou do lado do operador e mesmo assim há o que republicar: a
  // plataforma mudou como publica, e sem este estado a melhoria só chegaria ao agente quando
  // alguém republicasse por outro motivo.
  if (agent.needs_republish) return 'NEEDS_REPUBLISH';
  if (agent.publish_status === 'published') {
    if (!agent.enabled) return 'DISABLED';
    // Publicado e sem ingresso: ninguém recebe nada por ele. Era verde.
    if (agent.brain === 'inativo') return 'PUBLISHED_INERT';
    return 'PUBLISHED';
  }
  return 'DRAFT';
};

const STATUS_BADGES = {
  FAILED: 'bg-n-ruby-4 text-n-ruby-11',
  UNPUBLISHED: 'bg-n-amber-4 text-n-amber-11',
  // Âmbar pelo mesmo motivo do PUBLISHED_LEGACY: o agente ESTÁ no ar, mas não com o que a
  // plataforma sabe fazer hoje. Verde diria que está tudo certo.
  NEEDS_REPUBLISH: 'bg-n-amber-4 text-n-amber-11',
  PUBLISHING: 'bg-n-amber-4 text-n-amber-11',
  PUBLISHED: 'bg-n-green-4 text-n-green-11',
  // Âmbar, não verde. O rótulo já diz "robô externo", mas quem varre a lista lê a COR primeiro —
  // e verde igual ao do agente que roda de verdade reintroduz, em menor tipo, o mesmo
  // engano que esta distinção existe para desfazer.
  PUBLISHED_LEGACY: 'bg-n-amber-4 text-n-amber-11',
  // Rubi, não âmbar: âmbar é "no ar, mas não como deveria". Aqui não está no ar — o agente
  // parece publicado e não recebe uma mensagem sequer.
  PUBLISHED_INERT: 'bg-n-ruby-4 text-n-ruby-11',
  DISABLED: 'bg-n-slate-4 text-n-slate-11',
  DRAFT: 'bg-n-slate-4 text-n-slate-11',
};

const statusBadge = agent => STATUS_BADGES[statusKey(agent)];
const statusLabel = agent => t(`POWER_AGENTS.STATUS.${statusKey(agent)}`);
const statusTitle = agent =>
  agent.brain === 'externo' && agent.brain_destino
    ? t('POWER_AGENTS.RULER.SERVED_BY', { host: agent.brain_destino })
    : null;

async function fetchAgents() {
  isFetching.value = true;
  try {
    const data = await powerAgentsApi.index(accountId());
    agents.value = data.payload || [];
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.FETCH_ERROR')));
  } finally {
    isFetching.value = false;
  }
}

function openAgent(agent) {
  router.push({
    name: 'power_agents_settings',
    params: { accountId: accountId(), agentId: agent.id },
  });
}

function openCreate() {
  createForm.value = { bot_name: '' };
  createDialogRef.value.open();
}

async function confirmCreate() {
  isCreating.value = true;
  try {
    const created = await powerAgentsApi.create(accountId(), {
      bot_name: createForm.value.bot_name.trim(),
      slug: slugDoNome(createForm.value.bot_name.trim()),
      enabled: false,
      // Ship a sensible default pair so a fresh agent can respond without a trip
      // to the Models section first.
      config: {
        model_primary: DEFAULT_MODEL_PRIMARY,
        model_fallback: DEFAULT_MODEL_FALLBACK,
      },
    });
    createDialogRef.value.close();
    openAgent(created);
  } catch (e) {
    // O axios REJEITA em 4xx, entao o corpo do 422 nunca chegava no caminho feliz: o
    // `if (created.errors)` que existia aqui era codigo morto. A mensagem especifica
    // ("Bot name has already been taken") chegava no navegador e era jogada fora, e o
    // usuario via um "nao foi possivel criar" que nao diz o que corrigir.
    // Mesmo padrao ja usado em ToolsSection.vue.
    const doBackend = e?.response?.data?.errors;
    useAlert(
      (Array.isArray(doBackend) ? doBackend.join(', ') : doBackend) ||
        t('POWER_AGENTS.CREATE_DIALOG.CREATE_ERROR')
    );
  } finally {
    isCreating.value = false;
  }
}

async function confirmDelete() {
  const agent = selectedAgent.value;
  rowLoading.value[agent.id] = true;
  try {
    await powerAgentsApi.destroy(accountId(), agent.id);
    useAlert(t('POWER_AGENTS.DELETE.SUCCESS'));
    fetchAgents();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.DELETE.ERROR')));
  } finally {
    rowLoading.value[agent.id] = false;
    deleteDialogRef.value.close();
  }
}

onMounted(fetchAgents);
// Apagar o agente na página dele volta para cá por router.push. Se esta tela estiver em
// keep-alive, `onMounted` não roda de novo e o agente apagado continua na lista até um F5 —
// foi exatamente o que aconteceu ao apagar a Lui. `onActivated` cobre a volta.
onActivated(fetchAgents);
</script>

<template>
  <!-- Sem SettingsLayout de propósito. O `main` dele já é um painel com borda, bg-n-card e
       sombra — os MESMOS tokens do CardLayout — então card dentro dele fica invisível: com
       poucos agentes o que se vê é o painel, e o card some. Passa por lint e por build; só
       a tela revela, e só quando há UM item. Cabeçalho e grade montados direto. -->
  <div class="w-full h-full px-6 py-4 overflow-y-auto">
    <div class="flex flex-col w-full max-w-6xl gap-5 mx-auto">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.HEADER') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('POWER_AGENTS.DESCRIPTION') }}
          </p>
          <!-- A régua fica junto da descrição, não no contador da direita: é uma afirmação
             sobre a conta inteira, e o contador da direita segue o filtro da busca. -->
          <p
            v-if="regua"
            class="flex flex-wrap items-center mt-2 text-sm gap-x-3 gap-y-1"
          >
            <span v-if="regua.indefinida" class="text-n-slate-11">
              {{ t('POWER_AGENTS.RULER.UNKNOWN') }}
            </span>
            <template v-else>
              <span class="font-medium text-n-slate-12">
                {{
                  t('POWER_AGENTS.RULER.NATIVE', {
                    n: regua.nativos,
                    total: regua.total,
                  })
                }}
              </span>
              <span v-if="regua.externos" class="text-n-amber-11">
                {{ t('POWER_AGENTS.RULER.EXTERNAL', { n: regua.externos }) }}
              </span>
              <span v-if="regua.inertes" class="text-n-ruby-11">
                {{ t('POWER_AGENTS.RULER.INERT', { n: regua.inertes }) }}
              </span>
            </template>
          </p>
        </div>
        <div class="flex items-center gap-3">
          <!-- Contador do FILTRADO, não do total: com busca ativa o número precisa bater com
             o que está na tela, senão vira ruído. -->
          <span v-if="agents.length" class="text-sm text-n-slate-11">
            {{ t('POWER_AGENTS.COUNT', { n: filteredAgents.length }) }}
          </span>
          <Button
            v-if="isSuperAdmin"
            :label="t('POWER_AGENTS.CREATE')"
            size="sm"
            @click="openCreate"
          />
        </div>
      </header>

      <!-- A busca da barra de filtros padrão, só com a busca (sem pílulas). A lista é filtrada
         aqui mesmo, sem ir ao servidor: sem espera nova, o debounce fica em 0. -->
      <FilterBar
        :model-value="{ q: searchQuery }"
        :search="{ placeholder: t('POWER_AGENTS.SEARCH.AGENTS_PLACEHOLDER') }"
        :debounce="0"
        @update:model-value="valor => (searchQuery = valor.q)"
      />

      <div v-if="isFetching" class="text-sm text-n-slate-11">
        {{ t('POWER_AGENTS.LOADING') }}
      </div>

      <!-- Estado vazio explícito. Antes, sem agente nenhum, a tela renderizava um parágrafo
         em branco: o texto EMPTY só aparecia quando a BUSCA zerava a lista. -->
      <div
        v-else-if="!filteredAgents.length"
        class="flex flex-col items-center justify-center gap-2 py-16 text-center border border-dashed rounded-xl border-n-weak"
      >
        <i class="i-lucide-bot-message-square size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">{{ t('POWER_AGENTS.EMPTY') }}</p>
      </div>

      <div v-else class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <CardLayout
          v-for="agent in filteredAgents"
          :key="agent.id"
          class="cursor-pointer transition-shadow"
          @click="openAgent(agent)"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-center min-w-0 gap-3">
              <div
                class="flex items-center justify-center overflow-hidden text-base font-semibold rounded-lg size-12 shrink-0"
                :class="
                  agent.enabled
                    ? 'bg-n-accent-3 text-n-accent-11'
                    : 'bg-n-slate-4 text-n-slate-10'
                "
              >
                <img
                  v-if="agent.avatar_url"
                  :src="agent.avatar_url"
                  :alt="agent.bot_name || agent.slug"
                  class="object-cover size-full"
                />
                <template v-else>
                  {{ (agent.bot_name || agent.slug || '?')[0].toUpperCase() }}
                </template>
              </div>
              <div class="min-w-0">
                <h3 class="font-medium truncate text-n-slate-12">
                  {{ agent.bot_name || agent.slug }}
                </h3>
              </div>
            </div>
            <!-- O destino no title: o rótulo diz que está fora, e sem isto não diz onde
               procurar. Em produção é o n8n; no preview, o host neutralizado. -->
            <span
              class="text-xs px-2.5 py-1 rounded-full font-medium shrink-0"
              :class="statusBadge(agent)"
              :title="statusTitle(agent)"
            >
              {{ statusLabel(agent) }}
            </span>
          </div>

          <!-- Canais em que ele atende, com a marca real. Sem inbox, vira alerta. -->
          <div class="flex items-center justify-between gap-3 mt-2.5">
            <ChannelMarks :channels="agent.channels || []" />
            <!-- Qual LLM responde é dado de plataforma: para o cliente o servidor nem manda. -->
            <Label
              v-if="isSuperAdmin && agent.model_primary"
              compact
              :label="chipDoModelo(agent)"
              :title="tituloDoModelo(agent)"
              class="shrink-0"
            />
          </div>

          <!-- 30 dias de mensagens nas inboxes do agente. -->
          <div class="mt-2">
            <p class="text-[10px] uppercase tracking-wide text-n-slate-10">
              {{ t('POWER_AGENTS.CARD.TREND') }}
            </p>
            <AgentSparkline :series="agent.trend || []" />
          </div>

          <div
            class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-n-slate-11"
          >
            <!-- tools_count e guardrails_count já vinham no payload e a tabela nunca usou. -->
            <span>{{
              t('POWER_AGENTS.CARD.TOOLS', { n: agent.tools_count })
            }}</span>
            <span class="w-px h-3 bg-n-strong" />
            <span>
              {{
                t('POWER_AGENTS.CARD.GUARDRAILS', { n: agent.guardrails_count })
              }}
            </span>
          </div>
        </CardLayout>
      </div>
    </div>

    <Dialog
      ref="createDialogRef"
      :title="t('POWER_AGENTS.CREATE_DIALOG.TITLE')"
      :confirm-button-label="t('POWER_AGENTS.CREATE_DIALOG.CREATE')"
      :cancel-button-label="t('POWER_AGENTS.CREATE_DIALOG.CANCEL')"
      :is-loading="isCreating"
      :disable-confirm-button="!createForm.bot_name.trim()"
      @confirm="confirmCreate"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="createForm.bot_name"
          :label="t('POWER_AGENTS.CREATE_DIALOG.NAME_LABEL')"
          :placeholder="t('POWER_AGENTS.CREATE_DIALOG.NAME_PLACEHOLDER')"
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('POWER_AGENTS.DELETE.TITLE')"
      :description="
        t('POWER_AGENTS.DELETE.CONFIRM', {
          name: selectedAgent?.bot_name || selectedAgent?.slug,
        })
      "
      :confirm-button-label="t('POWER_AGENTS.DELETE.YES')"
      :cancel-button-label="t('POWER_AGENTS.DELETE.NO')"
      @confirm="confirmDelete"
    />
  </div>
</template>
